import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useCallback } from 'react';
import { Icon24Person } from '@figma/fpl-icons/icon-24-person';
import { imageOverlay } from './avatar-reset.css.js';

// eslint-disable-next-line fpl/fpl-data-attr
const DEFAULT_FALLBACK_ICON = jsx(Icon24Person, {});
/**
 * Resolves the active avatar content type and the corresponding ReactNode to render.
 * Manages src load state internally so callers can apply CSS classes based on type.
 *
 * When a src is provided, the fallback (icon → initial → unknown) renders beneath the image
 * rather than in its place, so it covers the load without gating it: an image the browser already
 * has paints on the first frame, and a slower one appears over the fallback as it arrives. `type`
 * names whichever of the two is actually visible, so it trails the image by a load event.
 *
 * Fallback order: src → icon → initial → fallbackIcon
 */
function useAvatarContent({ src, name, icon, fallbackIcon = DEFAULT_FALLBACK_ICON, initialClassName, }) {
    const initial = name ? getInitial(name) : undefined;
    const [loadedSrc, setLoadedSrc] = useState(null);
    const onLoad = useCallback(() => {
        if (src) {
            setLoadedSrc(src);
        }
    }, [src]);
    const isLoaded = loadedSrc === src;
    const [failedSrc, setFailedSrc] = useState(null);
    const onError = useCallback(() => {
        if (src) {
            setFailedSrc(src);
        }
    }, [src]);
    const hasFailed = failedSrc === src;
    const fallbackType = icon ? 'icon' : initial ? 'initial' : 'unknown';
    const fallbackContent = icon ? (icon) : initial ? (jsx("span", { "data-fpl-component": "", className: initialClassName, children: initial })) : (fallbackIcon);
    if (src) {
        return {
            type: isLoaded ? 'image' : fallbackType,
            content: (jsxs(Fragment, { children: [fallbackContent, !hasFailed && (jsx("img", { "data-fpl-component": "", src: src, alt: "", className: imageOverlay, onLoad: onLoad, onError: onError }))] })),
        };
    }
    return { type: fallbackType, content: fallbackContent };
}
/** Returns the first grapheme of a name (ignoring surrounding whitespace), upper-cased per locale rules. */
function getInitial(name) {
    for (const char of name.trim())
        return char.toLocaleUpperCase();
    return undefined;
}

export { useAvatarContent };
//# sourceMappingURL=avatar.hooks.js.map
